/**
 * Roles management component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { listRoles, getRole, createRole, updateRole, listPermissions, type Role, type RoleDetail, type CreateRoleRequest, type Permission } from '../../api/rbac';
import { useToastStore } from '../../stores/toast';
import { Shield, Plus, Edit, Trash2, Search, X, Check, Eye, FileEdit, Settings, Code } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { RoleSwitcher } from './RoleSwitcher';

// Role color mapping
const getRoleColor = (roleName: string) => {
  const name = roleName.toLowerCase();
  if (name.includes('viewer')) return { bg: 'bg-blue-50 dark:bg-blue-900/20', icon: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800', badge: 'info' };
  if (name.includes('writer')) return { bg: 'bg-green-50 dark:bg-green-900/20', icon: 'text-green-600 dark:text-green-400', border: 'border-green-200 dark:border-green-800', badge: 'success' };
  if (name.includes('admin') && name.includes('tech')) return { bg: 'bg-purple-50 dark:bg-purple-900/20', icon: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800', badge: 'warning' };
  if (name.includes('admin')) return { bg: 'bg-orange-50 dark:bg-orange-900/20', icon: 'text-orange-600 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800', badge: 'warning' };
  return { bg: 'bg-gray-50 dark:bg-gray-900/20', icon: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-800', badge: 'info' };
};

// Role icon mapping
const getRoleIcon = (roleName: string) => {
  const name = roleName.toLowerCase();
  if (name.includes('viewer')) return Eye;
  if (name.includes('writer')) return FileEdit;
  if (name.includes('admin') && name.includes('tech')) return Code;
  if (name.includes('admin')) return Settings;
  return Shield;
};

export function RolesManager() {
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { tenantId, projectId } = useWorkspace();

  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['roles', tenantId, projectId],
    queryFn: async () => {
      try {
        return await listRoles({ tenant_id: tenantId, project_id: projectId });
      } catch (err: any) {
        console.error('[RolesManager] Error loading roles:', err);
        // Don't throw - let React Query handle it
        throw err;
      }
    },
    enabled: true, // Always enabled
    retry: false,
    onError: (error: any) => {
      console.error('[RolesManager] Query error:', error);
      // Only show toast if it's not a 404 (which might be expected)
      if (error?.response?.status !== 404) {
        addToast({ 
          type: 'error', 
          message: error?.response?.data?.detail || 'Failed to load roles' 
        });
      }
    },
  });

  const filteredRoles = React.useMemo(() => {
    const filtered = roles?.filter(role =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
    
    // Sort by permission count (descending), then by display name
    return filtered.sort((a, b) => {
      const countA = a.permission_count || 0;
      const countB = b.permission_count || 0;
      
      if (countA !== countB) {
        return countB - countA; // More permissions first
      }
      
      // If same permission count, sort alphabetically by display name
      return a.display_name.localeCompare(b.display_name);
    });
  }, [roles, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Roles & Permissions</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage roles and their associated permissions
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="whitespace-nowrap"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Role Switcher */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <RoleSwitcher />
      </Card>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          type="text"
          placeholder="Search roles by name, display name, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Roles</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {roles?.length || 0}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">System Roles</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {roles?.filter(r => r.scope === 'system').length || 0}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Tenant Roles</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {roles?.filter(r => r.scope === 'tenant').length || 0}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Project Roles</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {roles?.filter(r => r.scope === 'project').length || 0}
          </div>
        </Card>
      </div>

      {/* Roles List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading roles...</p>
        </div>
      ) : error ? (
        <Card className="p-12 text-center">
          <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Error loading roles</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {(error as any)?.response?.data?.detail || 'An error occurred while loading roles'}
          </p>
        </Card>
      ) : filteredRoles.length === 0 ? (
        <Card className="p-12 text-center">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No roles found</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search terms' : 'Create your first role to get started'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredRoles.map((role) => (
            <RoleCard key={role.id} role={role} />
          ))}
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            addToast({ type: 'success', message: 'Role created successfully' });
          }}
        />
      )}
    </div>
  );
}

function CreateRoleModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState<CreateRoleRequest>({
    name: '',
    display_name: '',
    scope: 'tenant',
    description: '',
    permission_ids: [],
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [permissionSearch, setPermissionSearch] = useState('');

  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => listPermissions(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => createRole(data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      addToast({ 
        type: 'error', 
        message: error?.response?.data?.detail || 'Failed to create role' 
      });
    },
  });

  const { addToast } = useToastStore();

  const filteredPermissions = permissions?.filter(perm => {
    if (!perm) return false;
    const searchLower = permissionSearch.toLowerCase();
    return (
      (perm.display_name || '').toLowerCase().includes(searchLower) ||
      (perm.name || '').toLowerCase().includes(searchLower) ||
      (perm.resource_type || '').toLowerCase().includes(searchLower)
    );
  }) || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.display_name) {
      addToast({ type: 'error', message: 'Name and display name are required' });
      return;
    }

    // Validate name format (should be lowercase, alphanumeric, underscores, hyphens)
    if (!/^[a-z0-9_-]+$/.test(formData.name)) {
      addToast({ 
        type: 'error', 
        message: 'Name must be lowercase and contain only letters, numbers, underscores, and hyphens' 
      });
      return;
    }

    createMutation.mutate({
      ...formData,
      permission_ids: selectedPermissions,
    });
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const groupedPermissions = filteredPermissions.reduce((acc, perm) => {
    if (!perm) return acc;
    const key = perm.resource_type || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Sort permissions within each group and sort groups
  const sortedGroupedPermissions = Object.keys(groupedPermissions)
    .sort((a, b) => (a || '').localeCompare(b || ''))
    .reduce((acc, key) => {
      acc[key] = groupedPermissions[key].sort((a, b) => 
        (a.display_name || '').localeCompare(b.display_name || '')
      );
      return acc;
    }, {} as Record<string, Permission[]>);

  return (
    <div 
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Role</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="e.g., admin, editor, viewer"
                  required
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Lowercase letters, numbers, underscores, and hyphens only
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="e.g., Administrator, Editor, Viewer"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Scope <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="system">System</option>
                  <option value="tenant">Tenant</option>
                  <option value="project">Project</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  System: App-wide, Tenant: Per tenant, Project: Per project
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this role can do..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions ({selectedPermissions.length} selected)
              </label>
              
              {/* Permission Search */}
              <Input
                type="text"
                placeholder="Search permissions..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-gray-400" />}
                className="mb-3"
              />

              {/* Permissions List */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg max-h-64 overflow-y-auto">
                {permissionsLoading ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    Loading permissions...
                  </div>
                ) : Object.keys(groupedPermissions).length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No permissions found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-slate-700">
                    {Object.entries(groupedPermissions).map(([resourceType, perms]) => (
                      <div key={resourceType}>
                        <div className="px-3 py-2 bg-gray-50 dark:bg-slate-900/50 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {resourceType}
                        </div>
                        {perms.map((perm) => {
                          const isSelected = selectedPermissions.includes(perm.id);
                          return (
                            <label
                              key={perm.id}
                              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePermission(perm.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-600 rounded focus:ring-blue-500 dark:bg-slate-700 dark:checked:bg-blue-600"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {perm.display_name}
                                </div>
                                {perm.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {perm.description}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !formData.name || !formData.display_name}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Role'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RoleCard({ role }: { role: Role }) {
  const [showDetails, setShowDetails] = useState(false);
  const { data: roleDetail, isLoading: loadingDetails } = useQuery({
    queryKey: ['role', role.id],
    queryFn: () => getRole(role.id),
    enabled: showDetails,
  });

  const roleColor = getRoleColor(role.name);
  const RoleIcon = getRoleIcon(role.name);

  // Deduplicate and group permissions - prefer RFP-specific over generic
  const deduplicatedPermissions = React.useMemo(() => {
    if (!roleDetail?.permissions) return [];
    
    // Action mapping: generic -> RFP equivalent
    const actionMap: Record<string, string> = {
      'read': 'view',
      'write': 'write', // Some RFP permissions use 'write', others use 'create'/'edit'
      'create': 'create',
      'edit': 'edit',
      'approve': 'approve',
      'submit': 'submit',
      'manage': 'manage',
    };
    
    const permissionMap = new Map<string, typeof roleDetail.permissions[0]>();
    const processedGeneric = new Set<string>();
    
    // First pass: identify RFP permissions and mark their generic equivalents
    roleDetail.permissions.forEach(perm => {
      const permName = perm.name.toLowerCase();
      
      if (permName.startsWith('rfp.')) {
        // This is an RFP permission - find and mark generic equivalent
        const parts = permName.split('.');
        if (parts.length >= 3) {
          const resourceType = parts[1]; // e.g., "proposals" from "rfp.proposals.view"
          const rfpAction = parts[2]; // e.g., "view"
          
          // Find generic equivalent
          const genericAction = Object.entries(actionMap).find(
            ([_, rfp]) => rfp === rfpAction
          )?.[0] || rfpAction;
          
          const genericName = `${resourceType}.${genericAction}`;
          processedGeneric.add(genericName);
        }
        
        // Always include RFP permissions
        permissionMap.set(permName, perm);
      }
    });
    
    // Second pass: add generic permissions only if no RFP equivalent exists
    roleDetail.permissions.forEach(perm => {
      const permName = perm.name.toLowerCase();
      
      if (!permName.startsWith('rfp.') && !processedGeneric.has(permName)) {
        // Generic permission with no RFP equivalent - include it
        permissionMap.set(permName, perm);
      }
    });
    
    return Array.from(permissionMap.values());
  }, [roleDetail?.permissions]);

  // Group deduplicated permissions by normalized resource type
  const groupedPermissions = React.useMemo(() => {
    return deduplicatedPermissions.reduce((acc, perm) => {
      // Normalize resource type: "rfp.proposals" -> "proposals", "rfp.reports" -> "reports"
      let resourceType = perm.resource_type || 'other';
      if (resourceType.startsWith('rfp.')) {
        resourceType = resourceType.replace('rfp.', '');
      }
      
      if (!acc[resourceType]) acc[resourceType] = [];
      acc[resourceType].push(perm);
      return acc;
    }, {} as Record<string, typeof deduplicatedPermissions>);
  }, [deduplicatedPermissions]);

  return (
    <Card className={`overflow-hidden transition-all duration-200 hover:shadow-lg ${roleColor.border} border-2`}>
      <div className={`${roleColor.bg} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${roleColor.bg} ${roleColor.border} border`}>
              <RoleIcon className={`h-6 w-6 ${roleColor.icon}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {role.display_name}
                </h3>
                {role.is_system_role && (
                  <Badge variant="error" size="sm">System</Badge>
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                @{role.name}
              </div>
              {role.description && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-2">
                  {role.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge 
              variant={role.scope === 'system' ? 'error' : role.scope === 'tenant' ? 'info' : 'warning'}
              size="sm"
            >
              {role.scope}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowDetails(!showDetails)}
              className="whitespace-nowrap"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="p-6 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          {loadingDetails ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading permissions...</p>
            </div>
          ) : roleDetail ? (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Permissions ({deduplicatedPermissions.length} unique)
                </h4>
                <div className="space-y-4">
                  {Object.entries(groupedPermissions)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([resourceType, perms]) => {
                      // Clean up resource type name for display
                      const displayResourceType = resourceType
                        .split('.')
                        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                        .join(' ');
                      
                      return (
                        <div key={resourceType} className="border-l-2 border-gray-200 dark:border-slate-700 pl-4">
                          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                            {displayResourceType} ({perms.length})
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {perms
                              .sort((a, b) => a.display_name.localeCompare(b.display_name))
                              .map((perm) => {
                                // Clean up display name - remove "Rfp." prefix and format better
                                let displayName = perm.display_name;
                                if (displayName.startsWith('Rfp.') || displayName.startsWith('rfp.')) {
                                  displayName = displayName.replace(/^[Rr]fp\./, '').trim();
                                  // Capitalize first letter
                                  displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
                                }
                                
                                return (
                                  <Badge 
                                    key={perm.id} 
                                    variant="info" 
                                    size="sm"
                                    className="text-xs"
                                  >
                                    {displayName}
                                  </Badge>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Created: {new Date(role.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              Failed to load role details
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
